import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ImageCrop from '../../../components/ImageCrop';
import { getCroppedImageUrl } from '../../../components/ImageCrop/utils';
import { SaveButton, CancelButton } from '../../../components/Button/ActionButtons';

import {
  CropActions,
  CropFrame,
  CropHint,
  CropWarning,
  FieldLabel,
} from './styles';
import texts from './texts.json';

/** Fills `CropFrame`, which owns the height. Static so it is not a new object every render. */
const CROPPER_STYLE = { height: '100%', width: '100%' };

/*
  Progress photos, so JPEG: a lossless crop of a 1080-wide photo runs to several megabytes, which then
  has to be uploaded and downloaded again wherever the card is drawn. Asked for here rather than made
  the shared default, because the other cropper in the dashboard uploads under its source file's own
  extension and would then declare PNG while storing JPEG.
*/
const CROP_MIME_TYPE = 'image/jpeg';
const CROP_QUALITY = 0.9;

/**
 * Crops one picture before it is allowed into the task.
 *
 * This is the only privacy control in the feature: nothing detects or blurs faces, so the AC pulls
 * the frame in until the face is outside it. Only the crop is ever uploaded, which is why it happens
 * here rather than on the card — there is no un-cropped copy for a later card edit to reveal.
 *
 * One cropper is mounted at a time. Confirmed crops become plain data URLs, so a task with several
 * pictures does not hold several live Cropper instances.
 */
const CropStep = ({
  imageUrl,
  label,
  aspectRatio,
  requiresFaceBlur,
  onConfirm,
  onCancel,
}) => {
  const [cropper, setCropper] = useState();

  const handleConfirm = () => {
    const dataUrl = getCroppedImageUrl(cropper, CROP_MIME_TYPE, CROP_QUALITY);
    if (dataUrl) {
      onConfirm(dataUrl);
    }
  };

  return (
    <>
      <FieldLabel>{label}</FieldLabel>
      <CropFrame>
        {/*
          The style is load-bearing: `ImageCrop` applies it to the wrapper it renders, and without a
          height cropper.js sizes itself to the image's natural dimensions and overflows the frame.
          viewMode 1 keeps the crop box inside the photo, so a crop can never include empty canvas.
        */}
        <ImageCrop
          imageUrl={imageUrl}
          aspectRatio={aspectRatio}
          onCropperChange={setCropper}
          viewMode={1}
          style={CROPPER_STYLE}
        />
      </CropFrame>
      {requiresFaceBlur && <CropWarning>{texts.cropFaceBlurRequired}</CropWarning>}
      <CropHint>{texts.cropHint}</CropHint>
      <CropActions>
        <CancelButton onClick={onCancel}>{texts.cancel}</CancelButton>
        <SaveButton onClick={handleConfirm} icon={null} disabled={!cropper}>{texts.cropNext}</SaveButton>
      </CropActions>
    </>
  );
};

CropStep.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  requiresFaceBlur: PropTypes.bool,
};

CropStep.defaultProps = {
  aspectRatio: null,
  requiresFaceBlur: false,
};

export default CropStep;
